import { createFileRoute, redirect } from '@tanstack/react-router'
import { InstanceMonitoring } from '@/features/monitoring'
import type { MonitoringPresetId } from '@/features/monitoring/presets'

type MonitoringSearch = {
  tab?: 'metrics' | 'workload' | 'tools' | 'operations' | 'catalog'
  tool?: 'investigator' | 'planner' | 'timeline'
  /** Opens an existing governed proposal; does not dispatch it. */
  planId?: number
  view?: 'grid' | 'single'
  metric?: string
  range?: string
  preset?: MonitoringPresetId
  /** Preselects a metric in the Health tab's capacity trends section. */
  capacityMetric?: string
  /** Workload drill-down state, carried from the Right Now "Slow Queries" tile so
   *  the list shows exactly the digests the tile counted. */
  signal?: 'all' | 'slow' | 'regressed' | 'high_total_time'
  slowThresholdMs?: number
  sinceHours?: number
}

/** Coerces a search param to a positive finite number, or undefined. */
function positiveNumber(raw: unknown): number | undefined {
  const value = typeof raw === 'string' ? Number(raw) : raw
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : undefined
}

export const Route = createFileRoute('/_authenticated/instances/$instanceId/')({
  validateSearch: (search: Record<string, unknown>): MonitoringSearch => {
    const tab = search.tab
    const legacyTool =
      tab === 'investigator' || tab === 'planner' ? tab : undefined
    const tool = legacyTool ?? search.tool
    const view = search.view
    const metric = search.metric
    const range = search.range
    const preset = search.preset

    return {
      tab:
        tab === 'investigator' || tab === 'planner'
          ? 'tools'
          : tab === 'metrics' ||
              tab === 'workload' ||
              tab === 'tools' ||
              tab === 'operations' ||
              tab === 'catalog'
            ? tab
            : undefined,
      tool:
        tool === 'investigator' || tool === 'planner' || tool === 'timeline'
          ? tool
          : undefined,
      view: view === 'grid' || view === 'single' ? view : undefined,
      metric:
        typeof metric === 'string' && metric.trim().length > 0
          ? metric.trim()
          : undefined,
      range:
        typeof range === 'string' && range.trim().length > 0
          ? range.trim()
          : undefined,
      preset:
        preset === 'incident' ||
        preset === 'change' ||
        preset === 'audit' ||
        preset === 'operations'
          ? preset
          : undefined,
      capacityMetric:
        typeof search.capacityMetric === 'string' &&
        search.capacityMetric.trim().length > 0
          ? search.capacityMetric.trim()
          : undefined,
      signal:
        search.signal === 'all' ||
        search.signal === 'slow' ||
        search.signal === 'regressed' ||
        search.signal === 'high_total_time'
          ? search.signal
          : undefined,
      slowThresholdMs: positiveNumber(search.slowThresholdMs),
      sinceHours: positiveNumber(search.sinceHours),
      planId: Number.isSafeInteger(positiveNumber(search.planId))
        ? positiveNumber(search.planId)
        : undefined,
    }
  },
  beforeLoad: ({ search, params }) => {
    if (search.tab === 'operations') {
      throw redirect({
        to: '/instances/$instanceId/operations',
        params: { instanceId: params.instanceId },
      })
    }
    if (search.tab === 'catalog') {
      throw redirect({ to: '/command-center/data-catalog' })
    }
  },
  component: InstanceMonitoring,
})
